import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Briefcase, User, Wrench } from "lucide-react";
import { 
  getCommercialProducts, 
  getPersonalMobilityProducts,
  getIndustrialProducts,
  getProductsByType 
} from "@/data/productLines";
import ProductCardCompact from "./ProductCardCompact";
import ProductTypeFilterComponent from "./ProductTypeFilter";

const ProductsGrid = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  
  const commercialProducts = getCommercialProducts();
  const industrialProducts = getIndustrialProducts();
  const allPersonalProducts = getPersonalMobilityProducts();
  
  // Calculate product counts for filters
  const productCounts = useMemo(() => {
    const counts: Record<string, number> = {
      euc: allPersonalProducts.filter(p => p.productType === "euc").length,
      scooter: allPersonalProducts.filter(p => p.productType === "scooter").length,
      ebike: allPersonalProducts.filter(p => p.productType === "ebike").length,
    };
    return counts;
  }, [allPersonalProducts]);
  
  // Get filtered personal mobility products
  const filteredPersonalProducts = useMemo(() => {
    return getProductsByType(activeFilter);
  }, [activeFilter]);

  return (
    <section className="py-16 lg:py-24 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-eco-green/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Commercial / B2B Section */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Briefcase className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Commercial & B2B Mobility
              </h2>
              <p className="text-muted-foreground">
                Professional-grade vehicles for enterprise, government, and institutional clients
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {commercialProducts.map((product, index) => (
              <ProductCardCompact key={product.id} product={product} index={index} />
            ))}
          </div>
        </motion.div>

        {/* Industrial Equipment / XSTO Section */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500">
              <Wrench className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Industrial Equipment
              </h2>
              <p className="text-muted-foreground">
                XSTO stair climbers, motorized hand trucks, and utility platforms
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industrialProducts.map((product, index) => (
              <ProductCardCompact key={product.id} product={product} index={index} />
            ))}
          </div>
        </motion.div>

        {/* Personal Mobility Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-eco-green/10 text-eco-green">
              <User className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Personal Mobility
              </h2>
              <p className="text-muted-foreground">
                Premium electric unicycles, scooters, and e-bikes from King Song
              </p>
            </div>
          </div>

          {/* Product Type Filter */}
          <ProductTypeFilterComponent
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            productCounts={productCounts}
          />

          {/* Filtered Products Grid */}
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {filteredPersonalProducts.map((product, index) => (
              <ProductCardCompact key={product.id} product={product} index={index} />
            ))}
          </motion.div>
          
          {filteredPersonalProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products found in this category.</p>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ProductsGrid;
